import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import VPImage from '../assets/VP.webp'; 

// Quantum Hologram Effect
const hologramShimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 200px 0;
  }
`;

// Quantum Hologram Effect for Content
const QuantumHologram = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(var(--color-quantum-accent-rgb), 0.1) 25%,
      rgba(var(--color-quantum-accent-rgb), 0.2) 50%,
      rgba(var(--color-quantum-accent-rgb), 0.1) 75%,
      transparent 100%
    );
    animation: ${hologramShimmer} 3s linear infinite;
    z-index: -1;
    border-radius: 10px;
  }

  @media (max-width: 768px) {
    order: 1;
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-15px);
  }
  100% {
    transform: translateY(0);
  }
`;

const ImageContainer = styled.div`
  width: 400px;
  height: 400px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 4px 15px var(--color-shadow);
  z-index: 1;
  position: absolute;
  top: 55%;
  right: 10%;
  transform: translateY(-50%);

  @media (max-width: 768px) {
    width: 260px;
    height: 260px;
    position: relative;
    top: auto;
    right: auto;
    transform: none;
    margin: 1.5rem 0;
    order: 2;
  }

  @media (max-width: 576px) {
    width: 220px;
    height: 220px;
    margin: 1rem 0;
  }
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const QuantumParticles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle at 20% 80%, var(--color-quantum-glow) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, var(--color-quantum-glow) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, var(--color-quantum-glow) 0%, transparent 50%);
    animation: quantumFloat 20s ease-in-out infinite;
  }
  
  @keyframes quantumFloat {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    33% { transform: translateY(-20px) rotate(120deg); }
    66% { transform: translateY(10px) rotate(240deg); }
  }
`;

const HeroContainer = styled.section`
  height: 100vh;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0 10%;
  background: var(--color-bg);
  position: relative;
  overflow: hidden;
  min-width: 100vw;
  max-width: 100vw;
  scroll-snap-align: start;

  @media (max-width: 768px) {
    padding: 0 5%;
    justify-content: center;
    align-items: center;
    height: 100vh;
    flex-direction: column;
  }
`;

const BackgroundCircle = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  right: -200px;
  bottom: -200px;
  z-index: 1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  max-width: 600px;
  margin-right: 2rem;

  @media (max-width: 768px) {
    width: 100%;
    padding: 0 10px;
    text-align: center;
    margin-right: 0;
    order: 1;
  }
`;

const Greeting = styled(motion.h2)`
  font-size: 2rem;
  color: var(--color-primary);
  margin-bottom: 0.5rem;
  font-weight: 600;
  padding-top: 10px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    padding-top: 0;
  }

  @media (max-width: 576px) {
    font-size: 1.4rem;
  }
`;

const Name = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, var(--color-text) 0%, var(--color-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: var(--color-primary);

  @media (max-width: 768px) {
    font-size: 2.4rem;
  }

  @media (max-width: 576px) {
    font-size: 2.2rem;
  }
`;

const Title = styled(motion.h3)`
  font-size: 1.5rem;
  color: var(--color-secondary);
  margin-bottom: 2rem;
  font-weight: 400;
  min-height: 3rem;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    min-height: 2rem;
    margin-bottom: 0;
  }

  @media (max-width: 576px) {
    font-size: 1.1rem;
  }
`;


const TypewriterText = styled.span`
  position: relative;

  &::after {
    content: '|';
    color: var(--color-primary);
    animation: blink 1s infinite;
    font-weight: 400;
  }

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

const CTAButton = styled(motion.a)`
  display: inline-block;
  background-color: var(--color-primary);
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.8rem 2rem;
  border-radius: 30px;
  text-decoration: none;
  margin-right: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 4px 15px var(--color-shadow);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow:
      0 7px 15px var(--color-shadow),
      0 0 20px var(--color-quantum-glow),
      0 0 40px var(--color-quantum-glow);
  }

  @media (max-width: 768px) {
    font-size: 1.05rem;
    padding: 0.7rem 1.8rem;
  }

  @media (max-width: 576px) {
    font-size: 1rem;
    padding: 0.65rem 1.5rem;
  }
`;

const SecondaryButton = styled(motion.a)`
  display: inline-block;
  background-color: transparent;
  color: var(--color-primary);
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.8rem 2rem;
  border-radius: 30px;
  margin-bottom: 1rem;
  
  @media (max-width: 576px) {
    font-size: 1rem;
    padding: 0.7rem 1.8rem;
  }
  text-decoration: none;
  border: 2px solid var(--color-primary);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(79, 140, 255, 0.1);
    transform: translateY(-3px);
    box-shadow:
      0 0 20px var(--color-quantum-glow),
      0 0 40px var(--color-quantum-glow);
  }
`;

const ButtonWrapper = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  position: absolute;
  left: 10%;
  top: 68%;
  z-index: 2;

  @media (max-width: 768px) {
    position: relative;
    left: auto;
    top: auto;
    justify-content: center;
    order: 3;
  }
`;

const Hero = () => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [titleIndex, setTitleIndex] = useState(0);

  const titles = [
    "Tech Visionary",
    "Ex-Apple, Google, Amazon",
    "Built $B Products",
    "Crafting Next $T Technology"
  ];

  const currentTitle = titles[titleIndex];

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isDeleting && currentIndex < currentTitle.length) {
        setDisplayText(currentTitle.substring(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      } else if (isDeleting && currentIndex > 0) {
        setDisplayText(currentTitle.substring(0, currentIndex - 1));
        setCurrentIndex(currentIndex - 1);
      } else if (!isDeleting && currentIndex === currentTitle.length) {
        setTimeout(() => setIsDeleting(true), 2000);
      } else if (isDeleting && currentIndex === 0) {
        setIsDeleting(false);
        setTitleIndex((prev) => (prev + 1) % titles.length);
      }
    }, isDeleting ? 30 : 80);

    return () => clearTimeout(timer);
  }, [currentIndex, isDeleting, currentTitle, titleIndex, titles.length]);

  return (
    <HeroContainer id="home">

      <QuantumParticles />
      <QuantumHologram>
      <Content>
        <Greeting
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Hello, I'm
        </Greeting>
        <Name
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="gradient-text"
        >
          Surendar VP
        </Name>
        <Title
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <TypewriterText>
            {displayText}
          </TypewriterText>
        </Title>
      </Content>
      </QuantumHologram>
      <ImageContainer>
        <StyledImage src={VPImage} alt="Surendar VP" />
      </ImageContainer>
      <ButtonWrapper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.5 }}
      >
        <CTAButton href="https://www.linkedin.com/in/surendarvp" target="_blank" rel="noopener noreferrer">
          <i className="fab fa-linkedin" style={{ marginRight: '8px' }}></i>LinkedIn
        </CTAButton>
        <CTAButton href="https://pansas.org" target="_blank" rel="noopener noreferrer">
          <img src="/pansas_logo.webp" alt="PanSas" style={{ height: '20px', marginRight: '8px', verticalAlign: 'middle', filter: 'brightness(0) invert(1)' }} />
          PanSas
        </CTAButton>
      </ButtonWrapper>
      <BackgroundCircle />
    </HeroContainer>
  );
};

export default Hero;